import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    bulkResolveNeedsReview,
    getNeedsReviewBulkContext,
    NeedsReviewBulkContextRow,
    NeedsReviewResolution,
} from "../../../api/RosterApi";
import { PersonnelTypes } from "../../../constants/PersonnelTypes";
import { useAlert } from "../../../contexts/AlertsContext";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import LoadingGif from "../LoadingGif";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";
import BulkReviewResolveAccordionHeader from "./bulkReviewResolve/BulkReviewResolveAccordionHeader";
import BulkReviewResolveCollapseToolbar from "./bulkReviewResolve/BulkReviewResolveCollapseToolbar";
import {
    AccordionBody,
    AccordionItem,
    ActionRow,
    ActionSection,
    CommentSection,
    CommentText,
    ErrorText,
    LoadingContainer,
    ModalBody,
    StyledSelect,
} from "./bulkReviewResolve/bulkReviewResolveStyles";
import { formatMoveToChoiceLabel } from "./bulkReviewResolve/bulkReviewResolveUtils";

export type BulkReviewResolveRowState = NeedsReviewBulkContextRow & {
    actionType: "" | "RESTORE" | "MOVE";
    targetStatus: string;
};

export {
    formatBulkResolvePersonName,
    formatBulkResolvePersonNameOnly,
} from "./bulkReviewResolve/bulkReviewResolveUtils";

export const isBulkReviewResolveRowComplete = (row: BulkReviewResolveRowState): boolean => {
    if (row.actionType === "RESTORE") {
        return true;
    }
    if (row.actionType === "MOVE") {
        return Boolean(row.targetStatus);
    }
    return false;
};

export const formatRowChoiceLabel = (row: BulkReviewResolveRowState): string | null => {
    if (row.actionType === "RESTORE") {
        return "Restore to prior statuses";
    }
    if (row.actionType === "MOVE" && row.targetStatus) {
        return formatMoveToChoiceLabel(row.targetStatus);
    }
    return null;
};

type BulkReviewResolveNeedsReviewModalBodyProps = {
    personnelIds: number[];
    rows: BulkReviewResolveRowState[];
    setRows: React.Dispatch<React.SetStateAction<BulkReviewResolveRowState[]>>;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    error: string | null;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
};

const BulkReviewResolveNeedsReviewModalBody: React.FC<BulkReviewResolveNeedsReviewModalBodyProps> = ({
    personnelIds,
    rows,
    setRows,
    loading,
    setLoading,
    error,
    setError,
}) => {
    const { closeModal } = useGlobalModal();
    const showAlert = useAlert();
    const [expandedIds, setExpandedIds] = useState<number[]>([]);

    useEffect(() => {
        if (personnelIds.length === 0) {
            setLoading(false);
            setRows([]);
            return;
        }

        setLoading(true);
        setError(null);
        getNeedsReviewBulkContext(personnelIds)
            .then(response => {
                const orderMap = new Map(personnelIds.map((id, index) => [id, index]));
                const sorted = [...(response.rows || [])].sort(
                    (a, b) =>
                        (orderMap.get(a.personnelId) ?? 0) - (orderMap.get(b.personnelId) ?? 0),
                );
                setRows(
                    sorted.map(row => ({
                        ...row,
                        actionType: "",
                        targetStatus: "",
                    })),
                );
                setLoading(false);
            })
            .catch(() => {
                setError("Failed to load Needs Review context. Please try again.");
                setLoading(false);
                showAlert(
                    "Error loading Needs Review resolve context. Please contact BIGS Systems.",
                    "danger",
                );
            });
    }, [setLoading]);

    const toggleExpanded = useCallback((personnelId: number) => {
        setExpandedIds(prev =>
            prev.includes(personnelId)
                ? prev.filter(id => id !== personnelId)
                : [...prev, personnelId],
        );
    }, []);

    const removeRow = useCallback(
        (personnelId: number, e: React.MouseEvent) => {
            e.stopPropagation();
            setRows(prev => {
                const next = prev.filter(r => r.personnelId !== personnelId);
                if (next.length === 0) {
                    closeModal();
                }
                return next;
            });
            setExpandedIds(prev => prev.filter(id => id !== personnelId));
        },
        [closeModal, setRows],
    );

    const updateRow = useCallback(
        (personnelId: number, patch: Partial<BulkReviewResolveRowState>) => {
            setRows(prev =>
                prev.map(row => (row.personnelId === personnelId ? { ...row, ...patch } : row)),
            );
        },
        [setRows],
    );

    const collapseRow = useCallback((personnelId: number) => {
        setExpandedIds(prev => prev.filter(id => id !== personnelId));
    }, []);

    if (loading) {
        return (
            <ModalBody>
                <LoadingContainer>
                    <LoadingGif />
                </LoadingContainer>
            </ModalBody>
        );
    }

    if (error) {
        return (
            <ModalBody>
                <ErrorText>{error}</ErrorText>
            </ModalBody>
        );
    }

    return (
        <ModalBody>
            <BulkReviewResolveCollapseToolbar
                expandedCount={expandedIds.length}
                onCollapseAll={() => setExpandedIds([])}
            />
            {rows.map(row => {
                const expanded = expandedIds.includes(row.personnelId);
                const complete = isBulkReviewResolveRowComplete(row);
                const hasMoveTargets = (row.validMoveTargets?.length ?? 0) > 0;
                const choiceLabel = formatRowChoiceLabel(row);

                return (
                    <AccordionItem key={row.personnelId}>
                        <BulkReviewResolveAccordionHeader
                            personnelId={row.personnelId}
                            firstName={row.firstName}
                            middleName={row.middleName}
                            lastName={row.lastName}
                            affiliationAbbrev={row.affiliationAbbrev}
                            complete={complete}
                            choiceLabel={choiceLabel}
                            expanded={expanded}
                            onToggle={() => toggleExpanded(row.personnelId)}
                            onRemove={e => removeRow(row.personnelId, e)}
                        />
                        {expanded ? (
                            <AccordionBody>
                                <CommentSection>
                                    <CommentText>
                                        {row.needsReviewComment?.trim()
                                            ? row.needsReviewComment
                                            : "No comment recorded."}
                                    </CommentText>
                                </CommentSection>
                                <ActionSection>
                                    <ActionRow>
                                        <StyledSelect
                                            aria-label="Resolution action"
                                            value={
                                                row.actionType === "RESTORE"
                                                    ? "RESTORE"
                                                    : row.actionType === "MOVE"
                                                    ? "MOVE"
                                                    : ""
                                            }
                                            onChange={e => {
                                                const value = e.target.value;
                                                if (value === "RESTORE") {
                                                    updateRow(row.personnelId, {
                                                        actionType: "RESTORE",
                                                        targetStatus: "",
                                                    });
                                                    collapseRow(row.personnelId);
                                                } else if (value === "MOVE") {
                                                    updateRow(row.personnelId, {
                                                        actionType: "MOVE",
                                                        targetStatus: "",
                                                    });
                                                } else {
                                                    updateRow(row.personnelId, {
                                                        actionType: "",
                                                        targetStatus: "",
                                                    });
                                                }
                                            }}
                                        >
                                            <option value="">Select action...</option>
                                            <option value="RESTORE">
                                                Restore to prior statuses
                                            </option>
                                            {hasMoveTargets ? (
                                                <option value="MOVE">Move to...</option>
                                            ) : null}
                                        </StyledSelect>
                                        {row.actionType === "MOVE" ? (
                                            <StyledSelect
                                                aria-label="Target status"
                                                value={row.targetStatus}
                                                onChange={e => {
                                                    const targetStatus = e.target.value;
                                                    updateRow(row.personnelId, {
                                                        targetStatus,
                                                    });
                                                    if (targetStatus) {
                                                        collapseRow(row.personnelId);
                                                    }
                                                }}
                                            >
                                                <option value="">Select status...</option>
                                                {row.validMoveTargets.map(status => (
                                                    <option key={status} value={status}>
                                                        {status}
                                                    </option>
                                                ))}
                                            </StyledSelect>
                                        ) : null}
                                    </ActionRow>
                                </ActionSection>
                            </AccordionBody>
                        ) : null}
                    </AccordionItem>
                );
            })}
        </ModalBody>
    );
};

type BulkReviewResolveNeedsReviewModalFooterProps = {
    rows: BulkReviewResolveRowState[];
    loading: boolean;
    personnelAdvancedSearch(arg1: any, arg2: any, arg3: any, arg4: any): void;
    adminSelectedMenuOptions: Array<any>;
    adminAffiliationFilters: Array<any>;
    adminSearchText: Array<any> | string;
};

const BulkReviewResolveNeedsReviewModalFooter: React.FC<BulkReviewResolveNeedsReviewModalFooterProps> = ({
    rows,
    loading,
    personnelAdvancedSearch,
    adminSelectedMenuOptions,
    adminAffiliationFilters,
    adminSearchText,
}) => {
    const { closeModal, dispatch } = useGlobalModal();
    const showAlert = useAlert();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const allRowsComplete = useMemo(
        () => rows.length > 0 && rows.every(isBulkReviewResolveRowComplete),
        [rows],
    );

    const refreshSearch = () => {
        setTimeout(
            () =>
                personnelAdvancedSearch(
                    adminSearchText && adminSearchText.length !== 0 ? adminSearchText : null,
                    adminAffiliationFilters ? adminAffiliationFilters.map(opt => opt.orgId) : null,
                    adminSelectedMenuOptions.map(opt => opt.id),
                    PersonnelTypes.CLUB,
                ),
            1000,
        );
    };

    const handleResolve = async () => {
        setIsSubmitting(true);
        try {
            const resolutions: NeedsReviewResolution[] = rows.map(row => {
                if (row.actionType === "RESTORE") {
                    return { personnelId: row.personnelId, action: "RESTORE" };
                }
                return {
                    personnelId: row.personnelId,
                    action: "MOVE_TO_STATUS",
                    targetStatus: row.targetStatus,
                };
            });

            const result = await bulkResolveNeedsReview(resolutions);
            closeModal();
            dispatch({
                type: "openBulkReviewResolveResultsModal",
                bulkReviewResolveResults: result,
                personnelAdvancedSearch,
                adminSelectedMenuOptions,
                adminAffiliationFilters,
                adminSearchText,
            });
            refreshSearch();
        } catch {
            showAlert("Failed to resolve workflows. Please try again.", "danger");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="d-flex justify-content-end w-100">
            <div className="mr-2">
                <PrimaryButtonStyled
                    width={100}
                    height={26}
                    disabled={isSubmitting || loading || !allRowsComplete}
                    onClick={event => {
                        event.stopPropagation();
                        handleResolve();
                    }}
                >
                    {isSubmitting ? "Resolving..." : "Resolve"}
                </PrimaryButtonStyled>
            </div>
            <OutlinedButtonStyled
                width={80}
                height={26}
                disabled={isSubmitting}
                onClick={event => {
                    event.stopPropagation();
                    closeModal();
                }}
            >
                Cancel
            </OutlinedButtonStyled>
        </div>
    );
};

BulkReviewResolveNeedsReviewModalBody.displayName = "BodyContent";
BulkReviewResolveNeedsReviewModalFooter.displayName = "FooterContent";

export { BulkReviewResolveNeedsReviewModalBody, BulkReviewResolveNeedsReviewModalFooter };
